import React, { useState } from "react";

import SubtleButton from "../../../../../../components/Buttons/SubtleButton";
import DropdownSelect from "../../../../../../components/Inputs/DropdownSelect";
import SlideOverPanel from "../../../../../../components/Modals/SlideOverPanel";
import { useNumberInput } from "../../../../../../hooks/useNumberInput";
import { formatAddress } from "../../../RoutePage/Routes/utils";
import {
  UNASSIGNED_ROUTE_ID,
  useRouteBuilder,
} from "../../context/RouteBuilderContext";
import { locatePropertyVisit } from "../../lib/sessionOps";
import { incorporatesTime } from "../../lib/helpers";

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function PropertyDetailDrawer() {
  const {
    session,
    selectedPropertyVisitId,
    setSelectedPropertyVisitId,
    getPropertyForVisit,
    updateProperty,
    assignPropertyVisitToRoute,
    confirmMoveToUnassigned,
  } = useRouteBuilder();

  const property = selectedPropertyVisitId
    ? getPropertyForVisit(selectedPropertyVisitId)
    : undefined;
  const location =
    session && selectedPropertyVisitId
      ? locatePropertyVisit(session, selectedPropertyVisitId)
      : null;
  const currentRouteId = location?.routeId ?? null;
  const isLocked = Boolean(location?.lock);
  const routes = session?.routes ?? [];
  const [routeId, setRouteId] = useState(() => currentRouteId ?? "");

  const [notes, setNotes] = useState(() => property?.notes ?? "");
  const [serviceMinutes, setServiceMinutes] = useState(() =>
    Math.round((property?.estimatedServiceTimeSeconds ?? 1800) / 60),
  );
  const serviceMinutesInput = useNumberInput({
    value: serviceMinutes,
    onChange: setServiceMinutes,
    min: 5,
  });

  if (!property) return null;

  const showServiceTime = incorporatesTime(session?.inputs);
  const address = formatAddress(property.address);
  const customerPreference = property.dayOfWeek?.length
    ? property.dayOfWeek.map((d) => DAY_LABELS[d]).join(", ")
    : "None";

  const close = () => setSelectedPropertyVisitId(null);

  const save = async () => {
    const patch = {
      notes: notes || undefined,
      // With time off the field is hidden, so keep the stored estimate.
      ...(showServiceTime && {
        estimatedServiceTimeSeconds: serviceMinutes * 60,
      }),
    };
    if (
      currentRouteId &&
      routeId !== currentRouteId &&
      selectedPropertyVisitId
    ) {
      if (
        routeId === UNASSIGNED_ROUTE_ID &&
        !(await confirmMoveToUnassigned({
          name: property.name,
          locked: isLocked,
        }))
      ) {
        return;
      }
      assignPropertyVisitToRoute(selectedPropertyVisitId, routeId, patch);
      return;
    }
    updateProperty(property.jobId, patch);
    close();
  };

  return (
    <SlideOverPanel
      open
      testId="property-detail-drawer"
      zIndexClass="z-[70]"
      title={property.name}
      subtitle={
        <div className="flex flex-col gap-1">
          {address ? (
            <span className="text-sm text-slate-600">{address}</span>
          ) : null}
          <span className="text-xs capitalize text-slate-500">
            {property.serviceTimeSource}
          </span>
        </div>
      }
      onClose={close}
      footer={
        <>
          <SubtleButton buttonRole="secondary" onClick={close}>
            Cancel
          </SubtleButton>
          <SubtleButton buttonRole="primary" onClick={() => void save()}>
            Save
          </SubtleButton>
        </>
      }
    >
      {property.lastServicedAt ? (
        <p
          className="text-sm text-slate-600"
          data-testid="drawer-last-serviced"
        >
          Last serviced: {new Date(property.lastServicedAt).toLocaleString()}
        </p>
      ) : (
        <p
          className="text-sm text-slate-400"
          data-testid="drawer-last-serviced"
        >
          Never serviced
        </p>
      )}
      {property.serviceByDeadline ? (
        <p className="text-sm font-medium text-rose-700">
          Deadline: {new Date(property.serviceByDeadline).toLocaleString()}
        </p>
      ) : null}
      {property.siteMapAreaSqFt ? (
        <p className="text-sm text-slate-600">
          Site map area: {property.siteMapAreaSqFt.toLocaleString()} sq ft
        </p>
      ) : null}

      {currentRouteId && routes.length > 0 ? (
        <label className="block text-sm font-medium text-slate-700">
          Route
          <DropdownSelect
            label="Route"
            size="md"
            testId="drawer-assign-route"
            value={routeId}
            options={[
              { value: UNASSIGNED_ROUTE_ID, label: "Unassigned" },
              ...routes.map((route) => ({
                value: route.id,
                label: route.name,
              })),
            ]}
            onChange={setRouteId}
          />
          <span className="mt-1 block text-xs font-normal text-slate-500">
            {isLocked
              ? "Moving adds the property to the end of the selected route and clears its lock."
              : "Moving adds the property to the end of the selected route."}{" "}
            You can also drag it onto a route.
          </span>
        </label>
      ) : null}

      <div
        className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700"
        data-testid="customer-preference-days"
      >
        <span className="font-medium text-slate-800">
          Customer preference:{" "}
        </span>
        {customerPreference}
      </div>

      <label className="block text-sm font-medium text-slate-700">
        Notes
        <textarea
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
          rows={3}
          value={notes}
          disabled={property.companyId !== session?.companyId}
          onChange={(e) => setNotes(e.target.value)}
        />
      </label>

      {showServiceTime ? (
        <label className="block text-sm font-medium text-slate-700">
          Estimated service time (minutes)
          <input
            type="text"
            inputMode={serviceMinutesInput.inputMode}
            min={5}
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
            value={serviceMinutesInput.value}
            onChange={serviceMinutesInput.onChange}
            onFocus={serviceMinutesInput.onFocus}
            onBlur={serviceMinutesInput.onBlur}
          />
        </label>
      ) : null}
    </SlideOverPanel>
  );
}
